/**
 * Pure logic for the bulk attribute-intelligence research panel: scope-gating
 * (matches PersonaRewriteEnqueue's scopeKey/canWrite discipline — a comparison,
 * never a stored boolean) and the tick engine that drives a capped-concurrency,
 * staggered run over categories missing attribute intelligence.
 *
 * No React, no network — see AttributeIntelligenceBulkResearch.tsx for wiring.
 */

export interface PreviewState {
  /** The scope (sector+site) this preview was taken at. See canWrite. */
  scopeKey: string;
  count: number;
  items: string[];
}

export function scopeKey(sectorId: number | null, siteId: string | null): string {
  return `${sectorId ?? ''}|${siteId ?? ''}`;
}

export function canWrite(preview: PreviewState | null, currentScopeKey: string): boolean {
  if (!preview) return false;
  return preview.scopeKey === currentScopeKey;
}

/**
 * A response you cannot parse is not a preview of zero — see PersonaRewriteEnqueue's
 * parsePreview for the incident this mirrors. Fails closed on anything not
 * affirmatively well-formed.
 *
 * ⛔ AND "WELL-FORMED" MEANS *AGAINST THE REQUEST*, NOT MERELY IN ISOLATION (Fix Q,
 * codex-reviewer-2). The shape checks above answer "are these two arrays?" — they never
 * asked whether the two arrays are an answer to the question that was ASKED. Three
 * malformed responses passed cleanly and each one drove paid, sector-wide research:
 *   (a) a requested path omitted from BOTH lists — the preview silently under-counts,
 *       and the omitted category is never researched even though the operator asked;
 *   (b) a path in either list that was never requested — the run fires research for a
 *       category that is not on the operator's screen and never was;
 *   (c) the same path in BOTH lists — the preview contradicts itself, and which answer
 *       wins is an artefact of the reading order.
 * `missing` and `covered` must form an EXACT PARTITION of `requestedPaths`: every
 * requested path in exactly one list, and nothing else in either. Anything short of that
 * is a backend we do not understand, and a preview from a backend we do not understand is
 * not a licence to spend.
 *
 * @param requestedPaths the paths actually sent in the request this `json` answers.
 */
export function parseCoveragePreview(
  json: any,
  expectedScopeKey: string,
  requestedPaths: string[],
): PreviewState {
  if (!json || typeof json !== 'object') throw new Error('Coverage response was not an object');
  if (json.success !== true) throw new Error(json.error || 'Coverage check did not report success');
  if (!Array.isArray(json.missing)) throw new Error('Coverage response did not return a missing list');
  if (!Array.isArray(json.covered)) throw new Error('Coverage response did not return a covered list');

  // ⚠️ CHECKED, NOT COERCED. The old body ran `String(x)` over `missing`, so a null became
  // the literal string "null" and was previewed — and then POSTed — as a category path.
  const asPaths = (list: unknown[], label: string): string[] => list.map((x) => {
    if (typeof x !== 'string' || x.trim().length === 0) {
      throw new Error(`Coverage response returned a ${label} entry that is not a non-empty string`);
    }
    return x;
  });
  const missing = asPaths(json.missing, 'missing');
  const covered = asPaths(json.covered, 'covered');

  const missingSet = new Set(missing);
  if (missingSet.size !== missing.length) throw new Error('Coverage response listed a path twice in missing');
  const coveredSet = new Set(covered);
  if (coveredSet.size !== covered.length) throw new Error('Coverage response listed a path twice in covered');

  for (const path of missing) {
    if (coveredSet.has(path)) throw new Error(`Coverage response reported "${path}" as both missing and covered`);
  }

  const requestedSet = new Set(requestedPaths);
  for (const path of [...missing, ...covered]) {
    if (!requestedSet.has(path)) throw new Error(`Coverage response reported "${path}", which was never requested`);
  }
  for (const path of requestedSet) {
    if (!missingSet.has(path) && !coveredSet.has(path)) {
      throw new Error(`Coverage response did not account for "${path}" — it is in neither the missing nor the covered list`);
    }
  }

  return {
    scopeKey: expectedScopeKey,
    count: missing.length,
    items: missing,
  };
}

export type CoverageStatus = 'queued' | 'in-flight' | 'confirmed' | 'unconfirmed';

export interface RunRow {
  path: string;
  status: CoverageStatus;
  /** The tick this row was started on. null until it leaves 'queued'. */
  startedAtTick: number | null;
}

/** No throttle exists on the research service today; this is the safety valve. */
export const MAX_CONCURRENT = 4;
/** 18 ticks * 10s/tick = 3 minutes. */
export const MAX_TICKS_IN_FLIGHT = 18;

export interface TickDecision {
  toStart: string[];
  toCheck: string[];
  toTimeout: string[];
}

/**
 * Pure decision for one tick. Confirmed/unconfirmed rows are terminal and never
 * appear in any bucket. A row that times out this tick frees its slot for a queued
 * row to start on the SAME tick (toTimeout and toStart do not fight each other for
 * the cap — the cap is computed net of timeouts).
 */
export function computeTick(rows: RunRow[], currentTick: number, stopRequested: boolean): TickDecision {
  const inFlight = rows.filter(r => r.status === 'in-flight');
  const toTimeout = inFlight
    .filter(r => r.startedAtTick !== null && currentTick - r.startedAtTick >= MAX_TICKS_IN_FLIGHT)
    .map(r => r.path);
  const timeoutSet = new Set(toTimeout);
  const stillInFlight = inFlight.filter(r => !timeoutSet.has(r.path));
  const freeSlots = Math.max(0, MAX_CONCURRENT - stillInFlight.length);
  const queued = rows.filter(r => r.status === 'queued');
  const toStart = stopRequested ? [] : queued.slice(0, freeSlots).map(r => r.path);
  const toCheck = stillInFlight.map(r => r.path);
  return { toStart, toCheck, toTimeout };
}

export interface TickEffects {
  /** POSTs to start research for one category. Rejections are swallowed — a row
   *  that fails to start is judged purely by the timeout, same as one that starts
   *  fine but never confirms; there is no separate "failed to start" state. */
  startResearch: (path: string) => Promise<void>;
  /** Resolves true if the category is now covered. Rejections are treated as
   *  not-yet-covered, never as confirmed. */
  checkCoverage: (path: string) => Promise<boolean>;
}

/**
 * Runs one tick end to end: computes the decision, performs the side effects, and
 * returns the next row state. Fully testable without timers or React — a caller
 * (the component, or a test) drives ticks by calling this repeatedly.
 */
export async function runOneTick(
  rows: RunRow[],
  currentTick: number,
  stopRequested: boolean,
  effects: TickEffects,
): Promise<RunRow[]> {
  const decision = computeTick(rows, currentTick, stopRequested);
  const startSet = new Set(decision.toStart);
  const timeoutSet = new Set(decision.toTimeout);

  await Promise.all(decision.toStart.map(p => effects.startResearch(p).catch(() => {})));
  const checkResults = await Promise.all(
    decision.toCheck.map(async (p): Promise<[string, boolean]> => {
      const covered = await effects.checkCoverage(p).catch(() => false);
      return [p, covered];
    }),
  );
  const confirmedSet = new Set(checkResults.filter(([, covered]) => covered).map(([p]) => p));

  return rows.map((r) => {
    if (confirmedSet.has(r.path)) return { ...r, status: 'confirmed' };
    if (timeoutSet.has(r.path)) return { ...r, status: 'unconfirmed' };
    if (startSet.has(r.path)) return { ...r, status: 'in-flight', startedAtTick: currentTick };
    return r;
  });
}
